/**
 * Map Viewer — Leaflet map showing routes with color-coded polylines.
 * Matches the localhost:5000 DemoWebGUI map exactly.
 *
 * Key behaviors:
 * - Only shows routes for the ACTIVE DAY (not all days)
 * - Depot marker: large "D" icon with shadow
 * - Stop markers: 20px numbered circles with white border + shadow
 * - Route lines: weight 3.5, opacity 0.75
 * - Selection: hides other routes, fits bounds to selected
 */

import React, { useEffect, useRef, useState } from 'react';
import '../styles/MapViewer.css';

// Color constants matching CSS design tokens (used in Leaflet HTML strings where CSS vars aren't available)
const MAP_COLORS = {
  white: '#fff',
  text: '#202124',       // --ro-text
  textSecondary: '#5f6368', // --ro-text-secondary
  textMuted: '#80868b',  // --ro-text-muted
  border: '#e0e0e0',     // --ro-border
  borderLight: '#e8eaed', // --ro-border-light
  font: "'Segoe UI',system-ui,sans-serif", // --ro-font
};

export default function MapViewer({ solution, coords, geometries, selectedRoute, techColors, activeDay, addressLabels, settings }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const layersRef = useRef([]);
  const mountedRef = useRef(true);
  const [mapError, setMapError] = useState(false);

  const propsRef = useRef({ solution, coords, geometries, selectedRoute, techColors, activeDay, addressLabels, settings });
  propsRef.current = { solution, coords, geometries, selectedRoute, techColors, activeDay, addressLabels, settings };

  const needsFitRef = useRef(true);

  // Initialize map
  useEffect(() => {
    mountedRef.current = true;

    const initMap = () => {
      if (!mountedRef.current || !mapRef.current || mapInstance.current) return;
      const L = window.L;
      if (!L) return;

      const curCoords = propsRef.current.coords;
      const depot = curCoords?.['0'] || [28.9275, -82.0373];
      const map = L.map(mapRef.current).setView(depot, 10);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 18,
      }).addTo(map);
      mapInstance.current = map;
      drawRoutes();
    };

    const L = window.L;
    if (!L) {
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => initMap();
        script.onerror = () => { if (mountedRef.current) setMapError(true); };
        document.head.appendChild(script);
      }
    } else {
      initMap();
    }

    return () => {
      mountedRef.current = false;
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
      layersRef.current = [];
    };
  }, []);

  // Fit bounds when data or active day changes
  useEffect(() => {
    needsFitRef.current = true;
  }, [solution, coords, geometries, activeDay]);

  // Redraw when anything changes
  useEffect(() => {
    if (mapInstance.current) {
      // Invalidate size in case container resized (tab switch, etc.)
      mapInstance.current.invalidateSize();
      drawRoutes();
    }
  }, [solution, coords, geometries, selectedRoute, techColors, activeDay, addressLabels]);

  const drawRoutes = () => {
    const L = window.L;
    const map = mapInstance.current;
    const { solution: sol, coords: crd, geometries: geo, selectedRoute: sel, techColors: colors, activeDay: day, addressLabels: labels, settings: sett } =
      propsRef.current;
    if (!L || !map || !sol || !crd) return;

    // Clear existing layers
    layersRef.current.forEach(l => map.removeLayer(l));
    layersRef.current = [];

    const depot = crd['0'] || [28.9275, -82.0373];

    // Depot marker — large "D" with shadow (matches localhost:5000)
    const depotMarker = L.marker(depot, {
      icon: L.divIcon({
        className: '',
        html: `<div style="background:${MAP_COLORS.text};color:${MAP_COLORS.white};width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;border:2.5px solid ${MAP_COLORS.white};box-shadow:0 2px 6px rgba(0,0,0,0.4)">D</div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      }),
      zIndexOffset: 1000,
    }).bindTooltip(sett?.depot_address ? `Depot: ${sett.depot_address}` : 'Depot (start/end)').addTo(map);
    layersRef.current.push(depotMarker);

    // Filter routes to active day only (key difference from old version that showed all days)
    const filtered = (sol.routes || []).filter(r => {
      if (!r.stops?.length) return false;
      if (day && r.day !== day) return false;
      return true;
    });

    const bounds = L.latLngBounds([depot]);

    // Chem refill station marker (if configured in settings)
    if (sett?.chem_refill_lat && sett?.chem_refill_lon) {
      const chemPos = [sett.chem_refill_lat, sett.chem_refill_lon];
      const chemMarker = L.marker(chemPos, {
        icon: L.divIcon({
          className: '',
          html: `<div style="background:${MAP_COLORS.white};color:${MAP_COLORS.text};width:24px;height:24px;border-radius:4px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:11px;border:2px solid ${MAP_COLORS.text};box-shadow:0 2px 6px rgba(0,0,0,0.3)">C</div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        }),
        zIndexOffset: 900,
      }).bindTooltip(sett.chem_refill_address ? `Chem Refill: ${sett.chem_refill_address}` : 'Chemical Refill Station').addTo(map);
      layersRef.current.push(chemMarker);
      bounds.extend(chemPos);
    }

    // Lunch break marker — estimate position from selected route midpoint
    if (sett?.lunch_break_duration_minutes > 0 && sel && filtered.length > 0) {
      const selRoute = filtered.find(r => r.day === sel.day && r.technician_name === sel.technician);
      if (selRoute?.stops?.length > 2) {
        const midIdx = Math.floor(selRoute.stops.length / 2);
        const midStop = selRoute.stops[midIdx];
        const midCoord = crd[String(midStop)];
        if (midCoord) {
          const lunchMarker = L.marker(midCoord, {
            icon: L.divIcon({
              className: '',
              html: `<div style="background:#f59e0b;color:${MAP_COLORS.white};width:22px;height:22px;border-radius:4px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:11px;border:2px solid ${MAP_COLORS.white};box-shadow:0 2px 6px rgba(0,0,0,0.3)">L</div>`,
              iconSize: [22, 22],
              iconAnchor: [11, 11],
            }),
            zIndexOffset: 800,
          }).bindTooltip(`Lunch Break (${sett.lunch_break_duration_minutes} min)`).addTo(map);
          layersRef.current.push(lunchMarker);
        }
      }
    }

    // Draw each route
    filtered.forEach((route) => {
      const color = colors?.[route.technician_name] || '#999';
      const isSelected = sel &&
        sel.day === route.day && sel.technician === route.technician_name;

      // When a route is selected, hide all others entirely (like localhost:5000)
      if (sel && !isSelected) return;

      const group = L.layerGroup().addTo(map);
      layersRef.current.push(group);

      // Try pre-computed geometry
      const geoKey = `${route.day}_${route.technician_name}`;
      const geojson = geo?.[geoKey];

      if (geojson) {
        const geoLayer = L.geoJSON(geojson, {
          style: { color, weight: 3.5, opacity: 0.75 },
        });
        group.addLayer(geoLayer);
      } else {
        // Fallback: dashed straight lines
        const points = [depot];
        route.stops.forEach(si => {
          const c = crd[String(si)];
          if (c) points.push(c);
        });
        points.push(depot);
        const polyline = L.polyline(points, {
          color, weight: 2.5, opacity: 0.7, dashArray: '6,4',
        });
        group.addLayer(polyline);
      }

      // Stop markers — 20px numbered circles with white border + shadow
      route.stops.forEach((si, idx) => {
        const c = crd[String(si)];
        if (!c) return;
        bounds.extend(c);

        // Build rich popup content
        const labelObj = labels?.[String(si)];
        const addrLabel = typeof labelObj === 'string' ? labelObj : labelObj?.text || `Address #${si}`;
        const customerName = typeof labelObj === 'object' ? labelObj.name : '';
        const serviceTier = typeof labelObj === 'object' ? labelObj.tier : '';
        const fixedDay = typeof labelObj === 'object' ? labelObj.fixedDay : '';
        const fixedTech = typeof labelObj === 'object' ? labelObj.fixedTech : '';
        const lockStatus = typeof labelObj === 'object' ? labelObj.lockStatus : '';
        const popupLines = [
          `<div style="font-family:${MAP_COLORS.font};min-width:180px">`,
          `<div style="font-weight:700;font-size:13px;color:${color};margin-bottom:4px">${route.technician_name}</div>`,
        ];
        if (customerName) {
          popupLines.push(`<div style="font-size:12px;font-weight:600;color:${MAP_COLORS.text};margin-bottom:2px">${customerName}</div>`);
        }
        popupLines.push(`<div style="font-size:12px;color:${MAP_COLORS.text};margin-bottom:2px">${route.day} &middot; Stop ${idx + 1} of ${route.stops.length}${serviceTier ? ` &middot; <span style="background:${MAP_COLORS.borderLight};padding:1px 5px;border-radius:3px;font-size:10px;text-transform:uppercase">${serviceTier}</span>` : ''}</div>`);
        popupLines.push(`<div style="font-size:11px;color:${MAP_COLORS.text};margin-bottom:2px;max-width:220px;word-wrap:break-word">${addrLabel}</div>`);
        // Constraint badges
        const constraints = [];
        if (fixedDay) constraints.push(`Day: ${fixedDay}`);
        if (fixedTech) constraints.push(`Tech: ${fixedTech}`);
        if (lockStatus) constraints.push(lockStatus);
        if (constraints.length > 0) {
          popupLines.push(`<div style="font-size:10px;color:${MAP_COLORS.textSecondary};margin-top:3px">${constraints.map(c2 => `<span style="background:${MAP_COLORS.borderLight};padding:1px 5px;border-radius:3px;margin-right:4px">${c2}</span>`).join('')}</div>`);
        }
        if (c[0] != null && c[1] != null) {
          popupLines.push(`<div style="font-size:10px;color:${MAP_COLORS.textMuted};margin-top:2px">${c[0].toFixed(4)}, ${c[1].toFixed(4)}</div>`);
        }
        popupLines.push('</div>');

        const marker = L.marker(c, {
          icon: L.divIcon({
            className: '',
            html: `<div style="background:${color};color:${MAP_COLORS.white};width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;border:1.5px solid ${MAP_COLORS.white};box-shadow:0 1px 3px rgba(0,0,0,0.3)">${idx + 1}</div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10],
          }),
        }).addTo(map)
          .bindTooltip(`Stop ${idx + 1}: ${customerName ? customerName + ' — ' : ''}${addrLabel.length > 40 ? addrLabel.slice(0, 37) + '...' : addrLabel}`, { direction: 'top', offset: [0, -10] })
          .bindPopup(popupLines.join(''), { className: 'ro-map-popup' });
        layersRef.current.push(marker);
      });
    });

    // Fit bounds
    const doFit = () => {
      if (bounds.isValid()) {
        map.fitBounds(bounds, {
          padding: sel ? [50, 50] : [30, 30],
          maxZoom: sel ? 14 : undefined,
        });
      }
    };

    if (needsFitRef.current || sel) {
      doFit();
      needsFitRef.current = false;
    }

    // Fit Bounds button (always present, top-right)
    const FitControl = L.Control.extend({
      onAdd: function () {
        const btn = L.DomUtil.create('div', 'ro-map-fit-btn');
        btn.innerHTML = `<button title="Fit all routes" aria-label="Fit map to all routes" style="background:${MAP_COLORS.white};border:1px solid ${MAP_COLORS.border};border-radius:4px;padding:4px 8px;cursor:pointer;font-size:12px;font-weight:600;color:${MAP_COLORS.text};box-shadow:0 1px 4px rgba(0,0,0,0.15);line-height:1">\u2922</button>`;
        L.DomEvent.disableClickPropagation(btn);
        btn.querySelector('button').addEventListener('click', doFit);
        // hover styling handled by CSS (.ro-map-fit-btn button:hover)
        return btn;
      },
    });
    const fitCtrl = new FitControl({ position: 'topright' });
    fitCtrl.addTo(map);
    layersRef.current.push({ remove: () => map.removeControl(fitCtrl) });
  };

  if (mapError) {
    return (
      <div className="ro-map-container ro-map-error-state" role="alert">
        <div className="ro-map-error-text">Map failed to load. Check your internet connection.</div>
        <button
          className="ro-btn ro-btn-sm"
          onClick={() => {
            setMapError(false);
            const old = document.getElementById('leaflet-js');
            if (old) old.remove();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return <div ref={mapRef} className="ro-map-container" aria-label="Interactive route map" />;
}
